<template>
    <div
        :class="classMainCover"
        class="main-cover"
    >
        <div
            :class="classContentWrapperOverlay"
            class="content-wrapper"
        >
            <div
                :class="classContent"
                class="content"
            >
                <ContentComponentItem
                    v-for="(component, index) in data.content"
                    :key="index"
                    :data="component"
                    :has-eager-image="hasEagerImage"
                    :page-type="pageType"
                    background="BLUE"
                />
            </div>
        </div>

        <div class="main-cover-background">
            <template
                v-if="
                    background.__typename ===
                    'ComponentMainCoverBackgroundImage'
                "
            >
                <CmsImage
                    :data="background.backgroundImage"
                    :is-eager="hasEagerImage"
                    class="picture desktop"
                    fit="cover"
                    :sizes="['100vw']"
                />

                <CmsImage
                    :data="background.mobileBackgroundImage"
                    :is-eager="hasEagerImage"
                    class="picture mobile"
                    fit="cover"
                    :sizes="['100vw']"
                />
            </template>

            <template v-else>
                <CmsImage
                    :data="background.cover"
                    :is-eager="hasEagerImage"
                    alt="Main cover video poster"
                    class="picture desktop"
                    fit="cover"
                    :sizes="['100vw']"
                />
                <CmsImage
                    :data="background.mobileCover"
                    :is-eager="hasEagerImage"
                    alt="Main cover video poster"
                    class="picture mobile"
                    fit="cover"
                    :sizes="['100vw']"
                />

                <video
                    ref="video"
                    :src="background.video.url"
                    autoplay
                    class="video"
                    loop
                    muted
                    playsinline
                    @loadeddata="onLoaded"
                ></video>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, toRef, useTemplateRef } from 'vue'

import type { ComponentMainCoverFragment } from '@/graphql/api'
import { assertNever } from '@/helpers'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const { data, pageType } = defineProps<{
    data: ComponentMainCoverFragment
    pageType: PageType
    hasEagerImage: boolean
}>()

const video = useTemplateRef('video')

function onLoaded(): void {
    video.value?.play()
}

const background = toRef(() => data.background)

const classMainCover = computed(() => {
    const defaultHeight = ''

    switch (data.height) {
        case 'DEFAULT':
            return defaultHeight
        case 'SHORT':
            return 'short-height'
        case 'TALL':
            return 'tall-height'
        default:
            return assertNever(data.height, defaultHeight)
    }
})

const classContentWrapperOverlay = computed(() => {
    const defaultOverlay = 'overlay-bottom-to-top'

    switch (data.overlay) {
        case 'NONE':
            return ''
        case 'GRADIENT_TOP_TO_BOTTOM':
            return 'overlay-top-to-bottom'
        case 'GRADIENT_BOTTOM_TO_TOP':
            return defaultOverlay
        case 'SOLID':
            return 'overlay-solid'
        default:
            return assertNever(data.overlay, defaultOverlay)
    }
})

const classContent = computed(() => {
    const defaultWidth = ''

    switch (data.contentWidth) {
        case 'NORMAL':
            return defaultWidth
        case 'CONTENT_WIDTH':
            return 'content-width'
        case 'FULL':
            return 'full-width'
        default:
            return assertNever(data.contentWidth, defaultWidth)
    }
})
</script>

<style lang="scss" scoped>
.main-cover {
    width: 100%;
    position: relative;
    height: 520px;

    &.short-height {
        height: 270px;
    }

    &.tall-height {
        height: 770px;
    }

    @include min-width(1024) {
        max-height: 70vh;
        height: 700px;

        &.short-height {
            height: 490px;
        }

        &.tall-height {
            height: 910px;
        }
    }

    .main-cover-background {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;

        .picture,
        .video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: block;
        }

        .picture {
            &.desktop {
                display: none;

                @include min-width(768) {
                    display: block;
                }
            }

            &.mobile {
                display: block;

                @include min-width(768) {
                    display: none;
                }
            }
        }
    }

    .content-wrapper {
        position: relative;
        z-index: 2;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: flex-end;
        justify-content: center;

        &.overlay-bottom-to-top {
            background: linear-gradient(
                180deg,
                rgba($black, 0),
                rgba($black, 0.5)
            );
        }

        &.overlay-top-to-bottom {
            background: linear-gradient(
                0deg,
                rgba($black, 0),
                rgba($black, 0.5)
            );
        }

        &.overlay-solid {
            background: rgba($black, 0.25);
        }

        .content {
            width: 100%;
            padding: 60px 20px;

            :deep(.text) {
                max-width: 360px;
            }

            &.content-width {
                :deep(.text) {
                    max-width: 680px;
                }
            }

            &.full-width {
                :deep(.text) {
                    max-width: 1440px;
                }

                :deep(.th-title) {
                    max-width: 1440px;
                }
            }
        }
    }
}
</style>
